import { getLocalStorageKeyForStore } from '@coach-bot/data-access/shared/utils/local-storage';
import { DataType } from '@shared/extension/enums/data-type.enum';
import { StudyReelHostNames } from '@shared/extension/enums/study-reel-host-names.enum';
import { ONE_MINUTE_MS } from '@shared/utils/constants/time';
import { sendStudyReelMessage } from '../utils';
import { Services } from './services.service';
import { safeSentryCaptureException } from '../../../libs/shared/sentry/src';

const MAX_HEARTBEATS_TO_STORE = 30;
const NO_HEARTBEAT_THRESHOLD = 4 * ONE_MINUTE_MS;
export class HeartbeatService {
  private firstHeartbeatHandled = false;
  private readonly heartbeatsLocalStorageKey = getLocalStorageKeyForStore('heartbeats', HeartbeatService.name);

  constructor(
    private services: Services,
    private interval: number = ONE_MINUTE_MS
  ) {}

  start(): void {
    this.sendHeartbeat();
    setInterval(this.sendHeartbeat.bind(this), this.interval);
  }

  async getHeartbeats(): Promise<string[]> {
    return (
      (await this.services.localStorageService.getLocalStorage<string[]>(
        this.heartbeatsLocalStorageKey,
        DataType.Object
      )) ?? []
    );
  }

  private async sendHeartbeat(): Promise<void> {
    try {
      await sendStudyReelMessage(StudyReelHostNames.Heartbeat, { name: chrome.runtime.getManifest().name });
    } catch {}
    await this.handleHeartbeatMissed();
    this.firstHeartbeatHandled = true;
  }

  private async handleHeartbeatMissed(): Promise<void> {
    try {
      let heartbeats: string[] = await this.getHeartbeats();
      const now = new Date();
      if (heartbeats.length > 0 && this.firstHeartbeatHandled) {
        const lastHeartbeat = heartbeats.length > 0 ? heartbeats[0] : undefined;
        if (lastHeartbeat) {
          const lastHeartbeatDate = new Date(lastHeartbeat);
          const timeSinceLastHeartbeat = now.getTime() - lastHeartbeatDate.getTime();
          if (timeSinceLastHeartbeat > NO_HEARTBEAT_THRESHOLD) {
            const errorMessage = `Heartbeats missed: ${JSON.stringify([now.toISOString(), ...heartbeats])}`;
            console.warn(errorMessage);
            safeSentryCaptureException(new Error(errorMessage));
          }
        }
      }
      heartbeats.unshift(now.toISOString());
      if (heartbeats.length > MAX_HEARTBEATS_TO_STORE) {
        heartbeats = heartbeats.slice(0, MAX_HEARTBEATS_TO_STORE);
      }
      this.services.localStorageService.setLocalStorage(this.heartbeatsLocalStorageKey, heartbeats);
    } catch (error) {
      console.error('Error while handling heartbeat missed:', error);
    }
  }
}
